import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAxiosInterceptor } from "./axiosInstance";
import getSymbolFromCurrency from "currency-symbol-map";
import localeCurrency from "locale-currency";
import { useMemo } from "react";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import { postcodeValidator } from "postcode-validator";
import SmsDigitInput from './SmsDigitInput';
import MfaSetup from './MfaSetup';

countries.registerLocale(en);

const maskPhone = (phone) => {
    if (!phone || phone.length < 4) return '\u2022\u2022\u2022\u2022';
    const lastFour = phone.slice(-4);
    const maskedPart = '\u2022'.repeat(Math.max(phone.replace(/\D/g, '').length - 4, 3));
    return maskedPart + lastFour;
};

const UserProfile = () => {
    const [userData, setUserData] = useState({});
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSuperUser, setIsSuperUser] = useState(false);
    const [saveMessage, setSaveMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [switchingMfa, setSwitchingMfa] = useState(false);
    const [switchStep, setSwitchStep] = useState('verify');
    const [switchError, setSwitchError] = useState('');
    const [switchLoading, setSwitchLoading] = useState(false);
    const [switchTotpCode, setSwitchTotpCode] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const navigate = useNavigate();

    useAxiosInterceptor();

    const impersonatedUserId = localStorage.getItem("impersonatedUserId");
    const finalUserId = impersonatedUserId
        ? impersonatedUserId
        : localStorage.getItem("userId");

    // 1. Fetch user permissions to check if they're a superuser
    useEffect(() => {
        const fetchUserPermissions = async () => {
            try {
                const response = await api.get(`/auth/user/${finalUserId}`);
                setIsSuperUser(response.data.isSuperUser || false);
            } catch (error) {
                console.error("❌ Error fetching user permissions:", error);
            }
        };
        if (finalUserId) fetchUserPermissions();
    }, [finalUserId]);

    // 2. Fetch user data based on finalUserId
    useEffect(() => {
        if (!finalUserId) {
            setLoading(false);
            return;
        }
        const fetchUserData = async () => {
            try {
                const response = await api.get(`/auth/user/${finalUserId}`);
                setUserData(response.data);
            } catch (err) {
                console.error("❌ Error fetching user data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [finalUserId]);
    const currencySymbol = useMemo(() => {
        try {
            const currencyCode = localeCurrency.getCurrency(userData.country?.toUpperCase() || "US");
            return getSymbolFromCurrency(currencyCode) || "$";
        } catch {
            return "$";
        }
    }, [userData.country]);

    // 3. If superuser, fetch list of companies (for changing a user's company)
    useEffect(() => {
        if (!isSuperUser) return;
        const fetchCompanies = async () => {
            try {
                const response = await api.get("/company");
                setCompanies(response.data);
            } catch (err) {
                console.error("❌ Error fetching companies:", err);
            }
        };
        fetchCompanies();
    }, [isSuperUser]);

    // Update handleChange to filter out non-letter characters for the country field.
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "country") {
            const filteredValue = value.replace(/[^A-Za-z]/g, "");
            setUserData((prevData) => ({ ...prevData, [name]: filteredValue }));
        } else {
            setUserData((prevData) => ({ ...prevData, [name]: value }));
        }
    };

    const handleSwitchVerify = async (code) => {
        setSwitchError('');
        setSwitchLoading(true);
        try {
            await api.post('/mfa/verify', { token: code || switchTotpCode });
            setSwitchStep('setup');
        } catch (err) {
            setSwitchError(err.response?.data?.message || err.response?.data?.error || 'Invalid code. Please try again.');
        } finally {
            setSwitchLoading(false);
        }
    };

    const handleSwitchComplete = () => {
        setSwitchingMfa(false);
        setSwitchStep('verify');
        setSwitchTotpCode('');
        setSwitchError('');
        const fetchUserData = async () => {
            try {
                const response = await api.get(`/auth/user/${finalUserId}`);
                setUserData(response.data);
            } catch (err) {
                console.error('Error refreshing user data:', err);
            }
        };
        fetchUserData();
    };

    // Handle form submission to update user data.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveMessage("");

        const countryCode = userData.country?.trim().toUpperCase() || "";

        // Validate country
        if (!countries.isValid(countryCode)) {
            setSaveMessage("❌ Please enter a valid two-letter country code (e.g. US, MX, CA).");
            setSaving(false);
            return;
        }

        // Validate postal code (only if present)
        if (userData.postalCode && !postcodeValidator(userData.postalCode, countryCode)) {
            setSaveMessage("❌ Please enter a valid postal code for the selected country.");
            setSaving(false);
            return;
        }

        try {
            const payload = {
                firstName: userData.firstName,
                lastName: userData.lastName,
                linkedinProfile: userData.linkedinProfile || null,
                postalCode: userData.postalCode || null,
                country: countryCode,
                role: userData.role,
                careerSatisfaction: userData.careerSatisfaction || null,
                openToWork: userData.openToWork,
                minSalary: userData.minSalary || null,
                salaryTypePreference: userData.salaryTypePreference || "annual",
                willingToRelocate: userData.willingToRelocate ?? false,
                requiresSponsorship: userData.requiresSponsorship ?? false,
                maxCommuteRadius: userData.maxCommuteRadius || null,
                relocatesWithoutAssistance: userData.relocatesWithoutAssistance ?? null,
            };

            await api.put(`/auth/user/${finalUserId}`, payload);
            setSaveMessage("✅ Profile updated successfully.");
        } catch (error) {
            console.error("Failed to update user:", error);
            setSaveMessage("❌ Error updating profile.");
        }

        setSaving(false);
    };

    if (loading) {
        return <p className="text-lightgray">Loading user profile...</p>;
    }

    if (!finalUserId) {
        return <p className="text-red-500">No user ID available to load profile.</p>;
    }

    return (
        <div className="w-full max-w-2xl mx-auto px-4">
        <div className="p-6 bg-bluegray rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-orange mb-6">User Profile</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* First Name */}
                <div>
                    <label className="block text-lightgray">First Name</label>
                    <input
                        type="text"
                        name="firstName"
                        value={userData.firstName || ""}
                        onChange={handleChange}
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    />
                </div>

                {/* Last Name */}
                <div>
                    <label className="block text-lightgray">Last Name</label>
                    <input
                        type="text"
                        name="lastName"
                        value={userData.lastName || ""}
                        onChange={handleChange}
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    />
                </div>

                {/* Email (read-only) */}
                <div>
                    <label className="block text-lightgray">Email</label>
                    <input
                        type="email"
                        name="email"
                        value={userData.email || ""}
                        disabled
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    />
                </div>

                {/* LinkedIn Profile */}
                <div>
                    <label className="block text-lightgray">LinkedIn Profile</label>
                    {isSuperUser || finalUserId === localStorage.getItem("userId") ? (
                        <input
                            type="text"
                            name="linkedinProfile"
                            value={userData.linkedinProfile || ""}
                            onChange={handleChange}
                            placeholder="Enter LinkedIn URL"
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        />
                    ) : (
                        <p className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue">
                            {userData.linkedinProfile ? (
                                <a
                                    href={userData.linkedinProfile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-lightblue underline"
                                >
                                    {userData.linkedinProfile}
                                </a>
                            ) : (
                                "No LinkedIn Profile"
                            )}
                        </p>
                    )}
                </div>
                <div className="mb-4">
                    <label className="block text-lightgray font-semibold mb-1">
                        Minimum Salary ({currencySymbol})
                    </label>

                    <input
                        type="number"
                        name="minSalary"
                        value={userData.minSalary}
                        onChange={handleChange}
                        placeholder="e.g. 60000"
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-lightgray font-semibold mb-1">Salary Type Preference</label>
                    <select
                        name="salaryTypePreference"
                        value={userData.salaryTypePreference}
                        onChange={handleChange}
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    >
                        <option value="annual">Annual</option>
                        <option value="hourly">Hourly</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-lightgray font-semibold mb-1">Willing to Relocate?</label>
                    <select
                        name="willingToRelocate"
                        value={userData.willingToRelocate === null || userData.willingToRelocate === undefined ? '' : String(userData.willingToRelocate)}
                        onChange={(e) => setUserData({ ...userData, willingToRelocate: e.target.value === '' ? null : e.target.value === 'true' })}
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    >
                        <option value="">Not specified</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-lightgray font-bold mb-1">Requires visa sponsorship?</label>
                    <select
                        name="requiresSponsorship"
                        value={userData.requiresSponsorship ? "yes" : "no"}
                        onChange={(e) =>
                            setUserData({ ...userData, requiresSponsorship: e.target.value === "yes" })
                        }
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                    </select>
                </div>

                {userData.willingToRelocate === false && (
                    <div className="mb-4">
                        <label className="block text-lightgray font-semibold mb-1">Max Commute Radius (miles)</label>
                        <input
                            type="number"
                            name="maxCommuteRadius"
                            value={userData.maxCommuteRadius}
                            onChange={handleChange}
                            placeholder="e.g. 20"
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        />
                    </div>
                )}

                {userData.willingToRelocate === true && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-lightgray mb-1">
                            Would you relocate without relocation assistance?
                        </label>
                        <select
                            name="relocatesWithoutAssistance"
                            value={userData.relocatesWithoutAssistance === null || userData.relocatesWithoutAssistance === undefined ? "" : String(userData.relocatesWithoutAssistance)}
                            onChange={(e) => setUserData({ ...userData, relocatesWithoutAssistance: e.target.value === "" ? null : e.target.value === "true" })}
                            className="w-full p-2 bg-darkblue border border-gray-600 rounded text-white"
                        >
                            <option value="">Not specified</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    </div>
                )}

                {/* Country (two-letter code) */}
                <div>
                    <label className="block text-lightgray">Country (e.g. US, MX)</label>
                    <input
                        type="text"
                        name="country"
                        value={userData.country || ""}
                        onChange={(e) =>
                            setUserData((prevData) => ({
                                ...prevData,
                                country: e.target.value.toUpperCase(),
                            }))
                        }
                        maxLength={2}
                        autoComplete="country"
                        placeholder="e.g. US"
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        required
                    />
                </div>

                {/* Postal Code */}
                <div>
                    <label className="block text-lightgray">Postal Code</label>
                    <input
                        type="text"
                        name="postalCode"
                        value={userData.postalCode || ""}
                        onChange={handleChange}
                        autoComplete="postal-code"
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    />
                </div>

                {/* Role */}
                <div>
                    <label className="block text-lightgray">Role</label>
                    {isSuperUser ? (
                        <select
                            name="role"
                            value={userData.role || ""}
                            onChange={handleChange}
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        >
                            <option value="Candidate">Candidate</option>
                            <option value="Target">Target</option>
                            <option value="Recruiter">Recruiter</option>
                            <option value="Company Admin">Company Admin</option>
                            <option value="Super User">Super User</option>
                        </select>
                    ) : (
                        <p className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue">
                            {userData.role || "Unknown"}
                        </p>
                    )}
                </div>

                {/* Career Satisfaction */}
                <div>
                    <label className="block text-lightgray">
                        On a scale of 1-10, how satisfied are you with your career today? (Employers don't see this)
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((number) => (
                            <label key={number} className="flex items-center space-x-1 cursor-pointer">
                                <input
                                    type="radio"
                                    name="careerSatisfaction"
                                    value={number}
                                    checked={Number(userData.careerSatisfaction) === number} // ✅ Convert both to numbers
                                    onChange={handleChange}
                                    className="form-radio h-4 w-4 text-orange bg-deepblue border border-lightblue focus:ring focus:ring-orange"
                                />
                                <span className="text-lightgray">{number}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Open to Work */}
                <div>
                    <label className="block text-lightgray">Open to Work?</label>
                    <select
                        name="openToWork"
                        value={userData.openToWork ? "yes" : "no"}
                        onChange={(e) =>
                            setUserData((prev) => ({ ...prev, openToWork: e.target.value === "yes" }))
                        }
                        className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                    >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>
                </div>

                {/* Company (SuperUser Only) */}
                <div>
                    <label className="block text-lightgray">Company</label>
                    {isSuperUser ? (
                        <div>
                            <input
                                type="text"
                                placeholder="Search companies..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full p-2 mb-2 rounded bg-deepblue text-lightgray border border-lightblue"
                            />
                            <select
                                name="company"
                                value={userData.company || ""}
                                onChange={(e) =>
                                    setUserData((prev) => ({
                                        ...prev,
                                        company: e.target.value || null,
                                    }))
                                }
                                className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                            >
                                <option value="">No Company Assigned</option>
                                {companies
                                    .filter((c) =>
                                        c.name.toLowerCase().includes(searchQuery.toLowerCase())
                                    )
                                    .map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    ) : (
                        <p className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue">
                            {userData.company ? userData.company.name : "No Company Assigned"}
                        </p>
                    )}
                </div>
                {/* Security Section - only show for MFA-enabled users */}
                {userData.isMfaEnabled && (
                    <div className="border-t border-lightblue pt-4 mt-4">
                        <h3 className="text-xl font-bold text-orange mb-2">Security</h3>

                        {!switchingMfa ? (
                            <div>
                                <p className="text-lightgray text-sm">
                                    MFA Method: <span className="text-orange font-bold">
                                        {userData.mfaMethod === 'sms' ? `Text Message (${maskPhone(userData.phoneNumber)})` : 'Authenticator App'}
                                    </span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSwitchingMfa(true)}
                                    className="mt-3 py-2 px-4 bg-orange text-white font-bold rounded hover:bg-deeporange transition"
                                >
                                    Switch MFA Method
                                </button>
                            </div>
                        ) : switchStep === 'verify' ? (
                            <div>
                                <p className="text-lightgray text-sm mb-3">
                                    Enter your current MFA code to continue
                                </p>
                                {userData.mfaMethod === 'sms' ? (
                                    <>
                                        <SmsDigitInput
                                            onComplete={handleSwitchVerify}
                                            disabled={switchLoading}
                                            error={!!switchError}
                                        />
                                        {switchError && <p className="text-red-500 text-sm mt-2 text-center">{switchError}</p>}
                                    </>
                                ) : (
                                    <div>
                                        <input
                                            type="text"
                                            value={switchTotpCode}
                                            onChange={(e) => setSwitchTotpCode(e.target.value)}
                                            placeholder="Enter 6-digit code"
                                            maxLength="6"
                                            className="w-full p-2 rounded bg-deepblue text-white border border-lightblue text-center text-lg tracking-widest"
                                        />
                                        {switchError && <p className="text-red-500 text-sm mt-2">{switchError}</p>}
                                        <button
                                            type="button"
                                            onClick={() => handleSwitchVerify()}
                                            disabled={switchLoading || switchTotpCode.length < 6}
                                            className="mt-3 w-full py-2 bg-orange text-white font-bold rounded hover:bg-deeporange disabled:opacity-50"
                                        >
                                            {switchLoading ? 'Verifying...' : 'Verify'}
                                        </button>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => { setSwitchingMfa(false); setSwitchStep('verify'); setSwitchError(''); setSwitchTotpCode(''); }}
                                    className="mt-2 text-sm text-lightblue hover:text-white"
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <div>
                                <p className="text-lightgray text-sm mb-3">
                                    Choose your new MFA method
                                </p>
                                <MfaSetup onComplete={handleSwitchComplete} />
                                <button
                                    type="button"
                                    onClick={() => { setSwitchingMfa(false); setSwitchStep('verify'); setSwitchError(''); }}
                                    className="mt-2 text-sm text-lightblue hover:text-white"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={saving}
                    className="bg-orange text-white font-bold py-2 px-4 rounded hover:bg-deeporange disabled:opacity-50"
                >
                    {saving ? "Saving..." : "Update Profile"}
                </button>
                {saveMessage && (
                    <p className="mt-2 text-lightgray text-sm">{saveMessage}</p>
                )}
            </form>

            {/* Delete Account Section — only show for own profile, not when impersonating */}
            {!impersonatedUserId && (
                <div className="border-t border-red-800 pt-4 mt-6">
                    <h3 className="text-xl font-bold text-red-500 mb-2">Delete Account</h3>
                    {!showDeleteConfirm ? (
                        <div>
                            <p className="text-lightgray text-sm mb-3">
                                Permanently delete your account and all associated data. This action cannot be undone.
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="py-2 px-4 bg-red-700 text-white font-bold rounded hover:bg-red-600 transition"
                            >
                                Delete My Account
                            </button>
                        </div>
                    ) : (
                        <div>
                            <p className="text-lightgray text-sm mb-3">
                                Type <span className="font-bold text-red-400">DELETE</span> to confirm. This will permanently remove your account, profile data, and any applications.
                            </p>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder='Type "DELETE" to confirm'
                                className="w-full p-2 rounded bg-deepblue text-lightgray border border-red-700 mb-3"
                            />
                            {deleteError && <p className="text-red-500 text-sm mb-2">{deleteError}</p>}
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    disabled={deleteConfirmText !== "DELETE" || deleting}
                                    onClick={async () => {
                                        setDeleting(true);
                                        setDeleteError('');
                                        try {
                                            await api.delete(`/auth/user/${finalUserId}`);
                                            localStorage.clear();
                                            navigate("/signin");
                                        } catch (err) {
                                            setDeleteError(err.response?.data?.error || "Failed to delete account.");
                                            setDeleting(false);
                                        }
                                    }}
                                    className="py-2 px-4 bg-red-700 text-white font-bold rounded hover:bg-red-600 disabled:opacity-50 transition"
                                >
                                    {deleting ? "Deleting..." : "Permanently Delete"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(''); }}
                                    className="py-2 px-4 text-lightgray hover:text-white transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
        </div>
    );
};

export default UserProfile;
